import { useContext, useEffect, useState, useMemo, useId } from 'react';
import { css } from '@emotion/react';
import Checkbox from '@veeva/corgix/checkbox';
import ResponseErrorTypes from '../../../../constants/ResponseErrorTypes';
import ResponseStatus from '../../../../constants/ResponseStatus';
import {
    getErrorMessage,
    getFirstError,
    showErrorNotificationFromResponse,
} from '../../../../services/errorUtils';
import LoadingScreenContext from '../../../../services/LoadingScreenContext';
import AuthenticatedRouteLanguageContext from '../../../../services/AuthenticatedRouteLanguageContext';
import useNetworkErrorNotification from '../../../../services/useNetworkErrorNotification';
import hasValidASCIICharacters from '../../../../services/hasValidASCIICharacters';
import { useUserSessionContext } from '../../../../components/UserSessionContextWrapper';
import HelpIcon from '../../../../components/HelpIcon';
import EmailField from '../../../../components/EmailField';
import InvalidUsernameCharactersInlineMessage from '../../../../components/InvalidUsernameCharactersInlineMessage';
import { isValidEmail } from '../../../../services/emailUtils';
import { requestEmailUpdate } from '../../myAccountApi';
import Footer from '../Footer';
import { useFriendlyCaptcha } from '../../../../services/useFriendlyCaptcha';
import { CaptchaVerificationDialog } from '../../../../components/CaptchaVerification';

function EmailEdit({ currentEmail, onCancel, onSuccess, labels }) {
    const [newEmail, setNewEmail] = useState(currentEmail);
    const [errorMessage, setErrorMessage] = useState();
    const { handleResponseSessionErrors } = useUserSessionContext();
    const { showNotification: showNetworkErrorNotification } = useNetworkErrorNotification();
    const { verifyCaptcha, cancelCaptcha, showCaptcha, captchaWidget, captchaError } =
        useFriendlyCaptcha();
    const { setLoading } = useContext(LoadingScreenContext);
    const { messageLanguage } = useContext(AuthenticatedRouteLanguageContext);
    const [checked, setChecked] = useState(false);
    const checkedId = useId();

    const invalidUsernameCharsError = useMemo(
        () => <InvalidUsernameCharactersInlineMessage show />,
        [],
    );

    const onEmailChange = (email) => {
        setNewEmail(email);
        setErrorMessage('');
    };

    useEffect(() => {
        setErrorMessage('');
    }, [messageLanguage]);

    const handleVerify = async () => {
        if (!isValidEmail(newEmail)) {
            setErrorMessage(labels.invalidEmailError);
            return;
        }

        if (!hasValidASCIICharacters(newEmail)) {
            setErrorMessage(invalidUsernameCharsError);
            return;
        }

        setErrorMessage('');
        const frcToken = await verifyCaptcha();
        if (!frcToken) {
            return;
        }
        setLoading(true);
        const response = await requestEmailUpdate(newEmail, frcToken, messageLanguage);
        setLoading(false);

        if (handleResponseSessionErrors(response)) {
            return;
        }

        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                onSuccess(newEmail);
                break;
            case ResponseStatus.FAILURE: {
                const responseError = getFirstError(response);
                switch (responseError.type) {
                    case ResponseErrorTypes.VEEVAID_EMAIL_NOT_AVAILABLE:
                    case ResponseErrorTypes.VEEVAID_INVALID_RECAPTCHA: {
                        const message = await getErrorMessage(responseError, messageLanguage);
                        setErrorMessage(message);
                        break;
                    }
                    default: {
                        const i18nOptions = { language: messageLanguage };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                    }
                }
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNetworkErrorNotification();
                break;
            default:
            // No-op
        }
    };

    const handleChange = (e, newChecked) => {
        setChecked(newChecked);
    };

    return (
        <>
            <EmailField
                label={
                    <div>
                        {labels.primaryEmailLabel}{' '}
                        <HelpIcon message={labels.usernameTooltipLabel} />
                    </div>
                }
                value={newEmail}
                onChange={onEmailChange}
                errorMessage={errorMessage}
            />
            <div
                css={({ orangeLightest }) => css`
                    display: flex;
                    align-items: start;
                    background-color: ${orangeLightest};
                    padding: 1rem;
                    gap: 0.667rem;
                    border-radius: 8px;
                    box-sizing: border-box;

                    ul {
                        margin: 0;
                        padding-left: 2rem;
                    }
                `}
            >
                <Checkbox onChange={handleChange} checked={checked} id={checkedId} />
                <label htmlFor={checkedId}>
                    <span>{labels?.acknowledgementMainLabel}</span>
                    <ul>
                        <li>{labels?.acknowledgementBulletOneLabel}</li>
                        <li>{labels?.acknowledgementBulletTwoLabel}</li>
                    </ul>
                </label>
            </div>
            <Footer
                disableSubmit={newEmail === currentEmail || !checked}
                onCancel={onCancel}
                onSubmit={handleVerify}
                cancelLabel={labels.cancelLabel}
                submitLabel={labels.verifyLabel}
            />
            <CaptchaVerificationDialog
                show={showCaptcha}
                captchaWidget={captchaWidget}
                onCancel={cancelCaptcha}
                error={captchaError}
            />
        </>
    );
}

export default EmailEdit;
